import React from "react";
import { Box } from "@mui/material";
import {
  certificatePaperColour,
  type CertificateOrientation,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { Trophy } from "./Trophy";

/** The card's edge, this far in from the page edge. */
const CARD_INSET = 24;

/**
 * Soft overlapping circles in the card's top-left and bottom-right corners:
 * where each is centred (px from that corner), how big, and how strong.
 * Portrait pages are narrower, so theirs are smaller.
 */
const CORNER_CIRCLES: Record<
  CertificateOrientation,
  { x: number; y: number; r: number; opacity: number }[]
> = {
  landscape: [
    { x: 110, y: -10, r: 170, opacity: 0.18 },
    { x: -30, y: 110, r: 140, opacity: 0.28 },
  ],
  portrait: [
    { x: 80, y: -10, r: 130, opacity: 0.18 },
    { x: -24, y: 84, r: 110, opacity: 0.28 },
  ],
};

/** One corner's circles; the card's rounded, clipped edge trims them. */
const CornerCircles: React.FC<{
  colour: string;
  orientation: CertificateOrientation;
  corner: "top-left" | "bottom-right";
}> = ({ colour, orientation, corner }) => (
  <>
    {CORNER_CIRCLES[orientation].map(({ x, y, r, opacity }) => (
      <Box
        key={`${x},${y}`}
        sx={{
          position: "absolute",
          width: r * 2,
          height: r * 2,
          borderRadius: "50%",
          bgcolor: colour,
          opacity,
          ...(corner === "top-left"
            ? { left: x - r, top: y - r }
            : { right: x - r, bottom: y - r }),
        }}
      />
    ))}
  </>
);

/**
 * Breeze: a clean rounded card with a thin border on a pale wash, soft
 * circles in two corners and a trophy above the signatures.
 */
export const breezeDesign: CertificateDesign = {
  bodyFont: "nunito",
  Layout: ({ template, text, orientation }) => {
    const { accentColour: accent } = template;
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        inset={{
          landscape: "72px 104px 64px",
          portrait: "88px 72px 76px",
        }}
        centrepiece={<Trophy colour={accent} flourish="rays" />}
        frame={
          <>
            <Box
              aria-hidden="true"
              sx={{ position: "absolute", inset: 0, bgcolor: accent, opacity: 0.1 }}
            />
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                inset: `${CARD_INSET}px`,
                borderRadius: "24px",
                border: `3px solid ${accent}`,
                bgcolor: certificatePaperColour(template),
                overflow: "hidden",
              }}
            >
              <CornerCircles colour={accent} orientation={orientation} corner="top-left" />
              <CornerCircles colour={accent} orientation={orientation} corner="bottom-right" />
            </Box>
          </>
        }
      />
    );
  },
};
